import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  useToast,
  useColorModeValue,
  IconButton,
  Progress,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Image,
  Center,
  Flex
} from '@chakra-ui/react';
import { FiArrowLeft, FiUpload, FiX, FiPlus, FiVideo } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GENRES } from '../constants/genres';
import { CONTENT_RATINGS } from '../constants/contentRatings';
import { UploadBusinessLogic } from '../services/uploadBusinessLogic';
import { useUploadState } from '../hooks/useUploadState';

const UploadShort: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  
  const {
    formData,
    uploadState,
    updateFormData,
    updateUploadState,
    addError,
    clearErrors,
    resetForm
  } = useUploadState();

  const [currentStep, setCurrentStep] = useState(1);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const backButtonBg = useColorModeValue('purple.100', 'purple.800');
  const backButtonColor = useColorModeValue('purple.700', 'white');
  const backButtonHoverBg = useColorModeValue('purple.200', 'purple.700');

  const handleInputChange = (field: string, value: any) => {
    updateFormData(field, value);
    if (uploadState.errors[field]) {
      updateUploadState({
        errors: { ...uploadState.errors, [field]: '' }
      });
    }
  };

  const handleGenreToggle = (genre: string) => {
    const currentGenres = formData.genres;
    const isSelected = currentGenres.includes(genre as any);
    
    if (isSelected) {
      updateFormData('genres', currentGenres.filter(g => g !== genre));
    } else if (currentGenres.length < 3) {
      updateFormData('genres', [...currentGenres, genre as any]);
    }
  };

  const handleThumbnailSelect = (file: File) => {
    updateFormData('posterFile', file);
    const url = URL.createObjectURL(file);
    updateFormData('posterUrl', url);
  };

  const handleThumbnailRemove = () => {
    updateFormData('posterFile', null);
    updateFormData('posterUrl', '');
  };

  const handleFileSelect = async (files: File[]) => {
    if (!user) return;
    
    updateUploadState({
      uploadedFiles: [...uploadState.uploadedFiles, ...files]
    });

    try {
      await UploadBusinessLogic.handleFileUpload(
        files,
        user.uid,
        (progress) => updateUploadState({ uploadProgress: progress }),
        (url) => updateUploadState({
          uploadedFileUrls: [...uploadState.uploadedFileUrls, url]
        }),
        (error) => addError('files', error)
      );
    } catch (error) {
      console.error('Error uploading files:', error);
      addError('files', 'Failed to upload files');
    }
  };

  const handleFileRemove = (index: number) => {
    const newFiles = uploadState.uploadedFiles.filter((_, i) => i !== index);
    const newUrls = uploadState.uploadedFileUrls.filter((_, i) => i < newFiles.length);
    updateUploadState({
      uploadedFiles: newFiles,
      uploadedFileUrls: newUrls
    });
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.title.trim()) {
        addError('title', 'Title is required');
        return;
      }
      if (!formData.description.trim()) {
        addError('description', 'Description is required');
        return;
      }
      if (formData.genres.length === 0) {
        addError('genres', 'Please select at least one genre');
        return;
      }
    }
    setCurrentStep(2);
    // Scroll to top when moving to next step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    setCurrentStep(1);
    // Scroll to top when going back
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!user) return;

    clearErrors();
    updateUploadState({ isUploading: true, uploadProgress: 0 });

    try {
      // Ensure required fields have default values
      const shortData = {
        ...formData,
        category: 'short',
        language: formData.language || 'English',
        rating: (formData.rating || 'G') as any,
        visibility: formData.visibility || 'public'
      };

      const errors = UploadBusinessLogic.validateForm(
        shortData,
        uploadState.uploadedFiles,
        uploadState.uploadedFileUrls
      );

      if (Object.keys(errors).length > 0) {
        updateUploadState({ errors, isUploading: false });
        return;
      }

      await UploadBusinessLogic.handleSingleContentUpload(
        shortData,
        user.uid,
        uploadState.uploadedFiles,
        uploadState.uploadedFileUrls,
        (progress) => updateUploadState({ uploadProgress: progress })
      );

      toast({
        title: 'Short uploaded successfully!',
        description: 'Your short has been published.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      resetForm();
      navigate('/');
    } catch (error) {
      console.error('Error uploading short:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your short. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      updateUploadState({ isUploading: false });
    }
  };

  const renderStep1 = () => (
    <VStack gap={6} align="stretch">
      {/* Title */}
      <FormControl isInvalid={!!uploadState.errors.title}>
        <FormLabel color={labelColor} fontWeight="bold">
          Short Title *
        </FormLabel>
        <Input
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder="Enter short title"
          bg={inputBg}
          borderColor={borderColor}
        />
      </FormControl>

      {/* Description */}
      <FormControl isInvalid={!!uploadState.errors.description}>
        <FormLabel color={labelColor} fontWeight="bold">
          Description *
        </FormLabel>
        <Textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Enter short description"
          bg={inputBg}
          borderColor={borderColor}
          rows={4}
        />
      </FormControl>

      {/* Genres */}
      <FormControl isInvalid={!!uploadState.errors.genres}>
        <FormLabel color={labelColor} fontWeight="bold">
          Genres * (Select up to 3)
        </FormLabel>
        <Flex wrap="wrap" gap={2}>
          {GENRES.map(genre => (
            <Button
              key={genre}
              size="sm"
              variant={formData.genres.includes(genre as any) ? 'solid' : 'outline'}
              colorScheme="purple"
              onClick={() => handleGenreToggle(genre)}
              isDisabled={!formData.genres.includes(genre as any) && formData.genres.length >= 3}
            >
              {genre}
            </Button>
          ))}
        </Flex>
      </FormControl>

      {/* Cast, Director, Writer */}
      <HStack gap={4} align="start">
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Cast
          </FormLabel>
          <Input
            value={formData.cast}
            onChange={(e) => handleInputChange('cast', e.target.value)}
            placeholder="Actor names (comma separated)"
            bg={inputBg}
            borderColor={borderColor}
          />
        </FormControl>
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Director
          </FormLabel>
          <Input
            value={formData.director}
            onChange={(e) => handleInputChange('director', e.target.value)}
            placeholder="Director name(s)"
            bg={inputBg}
            borderColor={borderColor}
          />
        </FormControl>
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Writer
          </FormLabel>
          <Input
            value={formData.writer}
            onChange={(e) => handleInputChange('writer', e.target.value)}
            placeholder="Writer name(s)"
            bg={inputBg}
            borderColor={borderColor}
          />
        </FormControl>
      </HStack>

      {/* Content Rating */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Content Rating
        </FormLabel>
        <RadioGroup
          value={formData.rating}
          onChange={(value) => handleInputChange('rating', value)}
        >
          <HStack gap={4} wrap="wrap">
            {CONTENT_RATINGS.map(rating => (
              <Radio key={rating.value} value={rating.value} colorScheme="purple">
                {rating.label}
              </Radio>
            ))}
          </HStack>
        </RadioGroup>
      </FormControl>

      {/* Language */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Language
        </FormLabel>
        <Input
          value={formData.language}
          onChange={(e) => handleInputChange('language', e.target.value)}
          placeholder="English"
          bg={inputBg}
          borderColor={borderColor}
        />
      </FormControl>
    </VStack>
  );

  const renderStep2 = () => (
    <VStack gap={6} align="stretch">
      {/* Thumbnail Upload */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Thumbnail
        </FormLabel>
        <Box
          border="2px dashed"
          borderColor={formData.posterFile ? 'purple.300' : borderColor}
          borderRadius="lg"
          p={8}
          textAlign="center"
          bg={formData.posterFile ? 'purple.50' : inputBg}
          _dark={{ bg: formData.posterFile ? 'purple.900' : 'gray.700' }}
          cursor="pointer"
          _hover={{ borderColor: 'purple.400' }}
          transition="all 0.2s"
          onClick={() => document.getElementById('thumbnail-upload')?.click()}
          minH="200px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {formData.posterFile ? (
            <VStack gap={3}>
              <Box position="relative">
                <Image
                  src={formData.posterUrl!}
                  alt="Thumbnail preview"
                  maxH="150px"
                  maxW="200px"
                  objectFit="cover"
                  borderRadius="md"
                />
                <Button
                  position="absolute"
                  top={-2}
                  right={-2}
                  size="sm"
                  colorScheme="red"
                  variant="solid"
                  borderRadius="full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleThumbnailRemove();
                  }}
                >
                  <IconWrapper icon={FiX} size={12} />
                </Button>
              </Box>
            </VStack>
          ) : (
            <VStack gap={2}>
              <IconWrapper icon={FiPlus} size={32} color="gray.400" />
              <Text color="gray.500">Click to upload thumbnail</Text>
            </VStack>
          )}
          <Input
            id="thumbnail-upload"
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleThumbnailSelect(file);
            }}
            display="none"
          />
        </Box>
      </FormControl>

      {/* Video Upload */}
      <FormControl isInvalid={!!uploadState.errors.files}>
        <FormLabel color={labelColor} fontWeight="bold">
          Short Video *
        </FormLabel>
        <Box
          border="2px dashed"
          borderColor={uploadState.uploadedFiles.length > 0 ? 'purple.300' : borderColor}
          borderRadius="lg"
          p={8}
          textAlign="center"
          bg={uploadState.uploadedFiles.length > 0 ? 'purple.50' : inputBg}
          _dark={{ bg: uploadState.uploadedFiles.length > 0 ? 'purple.900' : 'gray.700' }}
          cursor="pointer"
          _hover={{ borderColor: 'purple.400' }}
          transition="all 0.2s"
          onClick={() => document.getElementById('video-upload')?.click()}
          minH="120px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {uploadState.uploadedFiles.length > 0 ? (
            <VStack gap={3}>
              {uploadState.uploadedFiles.map((file, index) => (
                <HStack key={index} gap={2}>
                  <IconWrapper icon={FiVideo} size={20} color="purple.600" />
                  <Text color="purple.600" fontWeight="medium">
                    {file.name}
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="red"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileRemove(index);
                    }}
                  >
                    <IconWrapper icon={FiX} size={12} />
                  </Button>
                </HStack>
              ))}
            </VStack>
          ) : (
            <VStack gap={2}>
              <IconWrapper icon={FiUpload} size={24} color="gray.400" />
              <Text color="gray.500">Click to upload short video</Text>
            </VStack>
          )}
          <Input
            id="video-upload"
            type="file"
            accept="video/*"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0) handleFileSelect(files);
            }}
            display="none"
          />
        </Box>
      </FormControl>
    </VStack>
  );

  if (!user) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Please log in to upload shorts.
          </Text>
          <Button onClick={() => navigate('/login')} colorScheme="purple">
            Log In
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
        <HStack gap={4} align="center" maxW="1200px" mx="auto">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate('/upload')}
            size="sm"
            bg={backButtonBg}
            color={backButtonColor}
            _hover={{ bg: backButtonHoverBg }}
            _dark={{ bg: 'purple.800', color: 'white', _hover: { bg: 'purple.700' } }}
          />
          <VStack align="start" gap={1}>
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Upload Short
            </Text>
            <Text color={labelColor}>
              Step {currentStep} of 2
            </Text>
          </VStack>
        </HStack>
      </Box>

      {/* Main Content */}
      <Box p={6} maxW="800px" mx="auto">
        {currentStep === 1 ? renderStep1() : renderStep2()}

        {/* Progress Bar */}
        {uploadState.isUploading && (
          <VStack gap={2} mt={6}>
            <Text fontSize="sm" color={labelColor}>
              Uploading... {Math.round(uploadState.uploadProgress)}%
            </Text>
            <Progress
              value={uploadState.uploadProgress}
              size="sm"
              colorScheme="purple"
              w="100%"
              borderRadius="full"
            />
          </VStack>
        )}

        {/* Navigation Buttons */}
        <HStack gap={4} mt={8}>
          {currentStep > 1 && (
            <Button
              variant="outline"
              colorScheme="purple"
              onClick={prevStep}
              flex="1"
            >
              Back
            </Button>
          )}
          <Button
            colorScheme="purple"
            onClick={currentStep === 2 ? handleSubmit : nextStep}
            isLoading={uploadState.isUploading}
            loadingText="Uploading..."
            flex="1"
          >
            {currentStep === 2 ? 'Upload Short' : 'Continue'}
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export default UploadShort;
